/**
 * QUEUE_DISPLAY — the "đơn đã sẵn sàng" board for takeaway.
 *
 * A wall screen, read from across a room by people who are not staff. So:
 * enormous numerals, only two columns (đang chuẩn bị / mời nhận), no chrome,
 * no interaction. It never needs a tap, which is why it has no buttons at all.
 *
 * DATA SOURCE: `GET /v1/branches/:branchId/pickup-queue` (routes/tables-ops.ts)
 * — today's TAKEAWAY/DELIVERY orders with per-item readiness computed
 * server-side, so an order split across several stations only shows "mời nhận"
 * once EVERY live item is READY or SERVED. Polled every 5 seconds; a failed
 * poll keeps the previous board (a stale board beats a blank wall).
 */

import { useAuth } from '../../auth/AuthProvider';
import { usePoll } from '../../lib/hooks';
import { hhmm } from '../../lib/format';
import type { PickupQueueResponse } from '../../lib/types';

const POLL_MS = 5_000;

export function QueueShell({ onActivateDevice }: { readonly onActivateDevice: () => void }): React.JSX.Element {
  const { api, me } = useAuth();
  const branchId = me?.session.activeBranchId ?? null;

  const queue = usePoll<PickupQueueResponse>(
    (signal) => api.tables.pickupQueue(branchId ?? '', { signal }),
    POLL_MS,
    [branchId],
    { enabled: branchId !== null },
  );

  if (branchId === null) {
    return (
      <div className="h-full space-y-4 overflow-y-auto bg-canvas p-6">
        <h1 className="text-2xl font-bold text-fg">Bảng gọi món</h1>
        <p className="text-sm text-fg-muted">Chưa chọn chi nhánh. Đăng nhập lại hoặc gán thiết bị vào chi nhánh.</p>
        <button type="button" className="text-sm underline" onClick={onActivateDevice}>
          Kích hoạt / gán lại thiết bị
        </button>
      </div>
    );
  }

  const orders = queue.data?.orders ?? [];
  const ready = orders.filter((o) => o.ready);
  const preparing = orders.filter((o) => !o.ready);

  return (
    <div className="dark grid h-full grid-cols-2 gap-6 bg-canvas p-8 text-fg">
      <section>
        <h2 className="mb-4 text-kds-xl font-black uppercase tracking-wide text-fg-muted">Đang chuẩn bị</h2>
        <ul className="flex flex-wrap gap-4">
          {preparing.map((order) => (
            <li
              key={order.orderId}
              className="rounded-xl bg-surface px-6 py-4 ring-1 ring-line"
            >
              <span className="block text-kds-2xl font-black tabular-nums text-fg-muted">{order.shortCode}</span>
              <span className="block text-center text-kds-sm text-fg-subtle">
                {order.readyItemCount + order.servedItemCount}/{order.itemCount} món
              </span>
            </li>
          ))}
        </ul>
        {queue.error !== null && queue.data === null ? (
          <p className="mt-4 text-kds-sm text-danger-fg">Không tải được bảng gọi món — sẽ tự thử lại.</p>
        ) : null}
      </section>

      <section>
        <h2 className="mb-4 text-kds-xl font-black uppercase tracking-wide text-success-fg">Mời nhận</h2>
        <ul className="flex flex-wrap gap-4">
          {ready.map((order) => (
            <li
              key={order.orderId}
              className="animate-pulse rounded-xl bg-success-solid px-6 py-4 shadow-pop"
            >
              <span className="block text-kds-2xl font-black tabular-nums text-white">{order.shortCode}</span>
              {order.customerName !== null && order.customerName !== '' ? (
                <span className="block text-center text-kds-sm font-semibold text-white">{order.customerName}</span>
              ) : null}
            </li>
          ))}
        </ul>
        <p className="mt-6 text-kds-sm text-fg-subtle">
          {queue.lastUpdatedAt === null ? '' : `Cập nhật ${hhmm(queue.lastUpdatedAt)} · tự làm mới mỗi 5 giây`}
        </p>
      </section>
    </div>
  );
}
